import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, MenuItem, Switch, FormControlLabel, CircularProgress, Chip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useHasPermission } from '../utils/permissionUtils';
import { getClient } from '../redux/ducks/client';
import UserAssignmentDialog from "./UserAssignmentDialog";
import MarkdownEditor from "./MarkdownEditor";
import styled from '@emotion/styled';
import { toLocalDay } from '../utils/dateUtils';
import TagPicker, { useTagDraft } from './TagPicker';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
}));

const FormField = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'top',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const Row = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'top',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
}));

const Column = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'top',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
}));

const UserChipsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const CreateAccountDialog = ({ isLoading, open, onClose, clientId, onSubmit, users }) => {
    const [isUserAssignmentOpen, setIsUserAssignmentOpen] = useState(false);
    const [accountUsers, setAccountUsers] = useState([]);
    const [activeUsers, setActiveUsers] = useState([]);
    const userClientId = useSelector(state => state.auth.currentUser.client?._id || state.auth.currentUser.client);
    const ownClient = clientId === userClientId;
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const canSeePrivate = hasPermission('canSeePrivate');
    const canCreateAccount = hasPermission('addAccount');
    const tagDraft = useTagDraft({ enabled: open, parentType: 'Client', parentId: clientId });
    const [accountData, setAccountData] = useState({
        client: clientId,
        name: '',
        active: true,
        status: 'Active',
        description: '',
        privacy: 'Public',
        website: '',
        startDate: toLocalDay(),
        billingTerms: {
            type: 'Parent',
            hours: 0,
            charge: 0,
            overage: false,
            frequency: 'Monthly',
            vip: false,
            level: 0,
        },
        accountManager: null,
        isTest: false,
        isInternal: false,
    });

    useEffect(() => {
        if (users) {
            if (!ownClient && clientId && userClientId) {
                dispatch(getClient(userClientId, (result) => {
                    if (result) {
                        let combinedUsers = [...result.users, ...users];
                        setActiveUsers(combinedUsers.filter((user) => user.active));
                    }
                }));
            } else {
                setActiveUsers(users.filter((user) => user.active));
            }
        }
        setAccountData(currentData => ({
            ...currentData,
            client: clientId
        }));
    }, [users, ownClient, clientId, userClientId, dispatch]);

    const handleOpenUserAssignment = () => {
        setIsUserAssignmentOpen(true);
    };

    const handleCloseUserAssignment = () => {
        setIsUserAssignmentOpen(false);
    };

    const handleAssign = (objectId, assignees) => {
        setAccountUsers(assignees);
    };

    return (
      <>
        <StyledDialog open={open} onClose={onClose}>
          <DialogTitle>Create Account</DialogTitle>
          <DialogContent>
            <FormField>
              <TextField label="Name" fullWidth value={accountData.name} onChange={(e) => setAccountData({ ...accountData, name: e.target.value })} />
            </FormField>
            <FormField>
              <MarkdownEditor initialContent={accountData.description} onChange={(value) => setAccountData({ ...accountData, description: value })} />
            </FormField>
            {(canSeeInternal || canSeePrivate) && (
              <FormField>
                <TextField label="Privacy" fullWidth value={accountData.privacy} onChange={(e) => setAccountData({ ...accountData, privacy: e.target.value })} select>
                  <MenuItem value="Public" selected>
                    Public
                  </MenuItem>
                  <MenuItem value="Internal">Internal</MenuItem>
                  {canSeePrivate && <MenuItem value="Private">Private</MenuItem>}
                </TextField>
              </FormField>
            )}
            <FormField>
              <TagPicker draft={tagDraft} parentType="Client" parentId={clientId} clientId={clientId || null} />
            </FormField>
            <FormField>
              <TextField label="Website" fullWidth value={accountData.website} onChange={(e) => setAccountData({ ...accountData, website: e.target.value })} />
            </FormField>
            {canSeeInternal && (
              <FormField>
                <FormControlLabel control={<Switch name="isTest" checked={accountData.isTest} onChange={(e) => setAccountData({ ...accountData, isTest: e.target.checked })} />} label="Test Account" />
              </FormField>
            )}
            <FormField>
              <Typography variant="subtitle1">Account Users</Typography>
              <UserChipsContainer>{accountUsers && accountUsers.map((user) => <Chip key={user._id} label={`${user.fullName}`} onDelete={() => setAccountUsers(accountUsers.filter((u) => u._id !== user._id))} />)}</UserChipsContainer>
              <Button onClick={handleOpenUserAssignment} variant="outlined">
                Add Users
              </Button>
            </FormField>
            {canSeeInternal && canCreateAccount && (
              <Row>
                <TextField label="Billing Terms" fullWidth value={accountData.billingTerms.type} onChange={(e) => setAccountData({ ...accountData, billingTerms: { ...accountData.billingTerms, type: e.target.value } })} select>
                  <MenuItem value="Parent">Parent</MenuItem>
                <MenuItem value="Hourly">Hourly</MenuItem>
                <MenuItem value="Retainer">Retainer</MenuItem>
                <MenuItem value="Internal">Internal</MenuItem>
                <MenuItem value="Non Billable">Non Billable</MenuItem>
              </TextField>
              {accountData.billingTerms.type === "Retainer" && (
                <Column>
                  <TextField label="Hours" fullWidth type="number" value={accountData.billingTerms.hours} onChange={(e) => setAccountData({ ...accountData, billingTerms: { ...accountData.billingTerms, hours: e.target.value } })} />
                  <TextField label="Charge" fullWidth value={accountData.billingTerms.charge} onChange={(e) => setAccountData({ ...accountData, billingTerms: { ...accountData.billingTerms, charge: e.target.value } })} />
                  <FormControlLabel
                    control={<Switch name="overage" checked={accountData.billingTerms.overage} onChange={(e) => setAccountData({ ...accountData, billingTerms: { ...accountData.billingTerms, overage: e.target.checked } })} />}
                    label="Overage"
                  />
                  <FormControlLabel control={<Switch name="vip" checked={accountData.billingTerms.vip} onChange={(e) => setAccountData({ ...accountData, billingTerms: { ...accountData.billingTerms, vip: e.target.checked } })} />} label="VIP" />
                </Column>
                )}
              </Row>
            )}
          </DialogContent>
          <DialogActions>
            {isLoading ? <CircularProgress /> : (
              <>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => onSubmit({ ...accountData, ...tagDraft.assignments }, accountUsers)} disabled={tagDraft.loading}>Create</Button>
              </>
            )}
          </DialogActions>
        </StyledDialog>

        <UserAssignmentDialog isOpen={isUserAssignmentOpen} onClose={handleCloseUserAssignment} objectId={clientId} objectType="account" onAssign={handleAssign} isNewObject={true} users={activeUsers} useRoles={true} />
      </>
    );
};

export default CreateAccountDialog; 